import type { OrderMessageWithUser } from 'src/api/db/apiOrderMessage'
import { AttachmentList } from 'src/shared/components/GlobalChatWidget/components/AttachmentList'
import { SafeMessageContent } from 'src/shared/components/GlobalChatWidget/components/SafeMessageContent'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Avatar } from 'src/shared/components/ui/avatar'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import {
  formatMessageAuthorName,
  formatMessageTimestamp,
  getMessageAuthorColor,
  getMessageAuthorInitials,
} from '../utils/chatMessageDisplay'
import { RequestTeamPingMessage } from './Chat/components/RequestTeamPingMessage'
import { TeamKlayderAvatar } from './Chat/components/TeamKlayderAvatar'
import { isTeamPingMessage } from './Chat/utils/teamPingMessage'

export const RequestChatMessage = ({
  message,
}: {
  message: OrderMessageWithUser
}) => {
  const { t } = useLocalization()

  if (isTeamPingMessage(message)) {
    return <RequestTeamPingMessage message={message} />
  }

  const authorName = formatMessageAuthorName(message, t)
  const authorInitials = getMessageAuthorInitials(authorName)
  const authorColor = getMessageAuthorColor(message, authorName)
  const hasContent = Boolean(message.content?.trim())
  const attachmentUrls = message.attachment_urls ?? []
  const isTeamMember =
    message.role !== 'customer' && message.role !== 'system'

  return (
    <Row className="group gap-3 px-3 py-1.5 transition-colors hover:bg-muted/30">
      {isTeamMember ? (
        <TeamKlayderAvatar />
      ) : (
        <Avatar size="sm" className="mt-0.5 h-8 w-8 rounded-full border-0">
          <div
            className="flex h-full w-full items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{ backgroundColor: authorColor }}
          >
            {authorInitials}
          </div>
        </Avatar>
      )}

      <Column className="min-w-0 flex-1 gap-0.5">
        <Row className="items-baseline gap-2">
          <span className="text-sm font-semibold text-foreground">
            {authorName}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatMessageTimestamp(message.created_at)}
          </span>
        </Row>

        {hasContent && (
          <SafeMessageContent
            content={message.content}
            className="text-sm leading-relaxed text-foreground [&_p]:m-0 [&_a]:text-primary"
          />
        )}

        {attachmentUrls.length > 0 && (
          <AttachmentList
            urls={attachmentUrls}
            className="mt-2 flex-wrap gap-2"
            linkClassName="block"
          />
        )}
      </Column>
    </Row>
  )
}
